import { validateDiscoveryResponse } from '../03-feed-session.mjs';
import { createRecommendations } from '../ui/18-recommendations.mjs';
/** Authorized global search presentation. Search requests are actor/query/route scoped;
 * stale responses never paint another session. Results use links to canonical profile/post
 * views rather than granting new relationship or media permissions. */
export function createSearch(ctx) {
  const recommendations = createRecommendations(ctx);
  let generation = 0;
  function renderSearch() {
    recommendations.dispose();
    const current = ++generation;
    const actor = ctx.state.me.id;
    const contentScope = ctx.contentScope ?? 'ordinary';
    const query = ctx.query.trim();
    ctx.renderContentScope?.(ctx.view);
    ctx.view.append(
      ctx.e(
        'h1',
        'search-heading',
        ctx.uiText(
          contentScope === 'learning' ? 'Search learning readings' : 'Search people and posts',
        ),
      ),
    );
    const form = ctx.e('form', 'people-search');
    const input = ctx.field('input', {
      value: query,
      maxLength: 120,
      minLength: 2,
      required: true,
      placeholder: ctx.uiText(
        contentScope === 'learning' ? 'Search learning readings' : 'Search people and posts',
      ),
      ariaLabel: ctx.uiText(
        contentScope === 'learning' ? 'Search learning readings' : 'Search people and posts',
      ),
    });
    const submit = ctx.e('button', 'secondary-button', ctx.uiText('Search'));
    form.append(input, submit);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      ctx.query = input.value.trim();
      const draft = globalThis.document?.querySelector?.('#search');
      if (draft) draft.value = ctx.query;
      ctx.render();
    });
    ctx.view.append(form);
    const results = ctx.e('div', 'search-results');
    results.setAttribute('aria-live', 'polite');
    ctx.view.append(results);
    if ([...query].length < 2) {
      if (query)
        results.append(
          ctx.e('p', 'subtle', ctx.uiText('Enter at least two characters to search.')),
        );
      recommendations.mount(results, { context: 'search', title: 'Explore' });
      return;
    }
    results.append(ctx.e('p', 'subtle', ctx.uiText('Searching…')));
    const active = () =>
      current === generation &&
      ctx.state?.me?.id === actor &&
      ctx.route()[0] === 'search' &&
      ctx.query.trim() === query &&
      (ctx.contentScope ?? 'ordinary') === contentScope;
    ctx
      .api(`/api/search?contentScope=${contentScope}&q=${encodeURIComponent(query)}`)
      .then((response) => {
        if (!active()) return;
        validateDiscoveryResponse(response, contentScope, ctx.discoveryPolicyVersion);
        results.replaceChildren();
        results.append(ctx.sectionTop(ctx.uiText('Posts'), String(response.posts.length)));
        response.posts.forEach((post) => {
          const card = ctx.e('article', 'search-post-result');
          const author = ctx.who(post.authorId);
          const content = ctx.e('div', 'search-post-copy');
          content.append(
            ctx.link(author?.name ?? ctx.uiText('View profile'), `#/profile/${post.authorId}`),
          );
          if (post.title) content.append(ctx.e('h3', '', post.title));
          content.append(ctx.e('p', 'search-post-excerpt', post.text));
          const image = post.media?.find((media) => media.kind === 'image');
          if (image || post.image)
            card.append(
              ctx.field('img', {
                src: ctx.photoSource(image?.url ?? post.image, 640, image?.variants),
                alt: image?.alt ?? post.alt ?? '',
                loading: 'lazy',
                decoding: 'async',
                className: 'search-post-thumbnail',
              }),
            );
          content.append(
            ctx.link(ctx.uiText('View post'), `#/post/${post.id}`, 'secondary-button'),
          );
          card.append(content);
          results.append(card);
        });
        if (!response.posts.length) results.append(ctx.empty(ctx.uiText('No matching posts.')));
        if (response.postsHasMore)
          results.append(
            ctx.e('p', 'subtle', ctx.uiText('More matches exist. Refine your search.')),
          );
        results.append(ctx.sectionTop(ctx.uiText('People'), String(response.people.length)));
        const people = ctx.e('div', 'people-grid');
        const cards = response.people.map((user) => {
          const card = ctx.e('article', 'person-card');
          card.append(
            ctx.avatar(user),
            ctx.append(ctx.e('h2'), ctx.link(user.name, `#/profile/${user.id}`)),
            ctx.e('p', 'post-meta', user.handle ? `@${user.handle}` : ''),
          );
          if (user.bio) card.append(ctx.e('p', '', user.bio));
          return card;
        });
        people.append(...cards.slice(0, 6));
        results.append(
          response.people.length ? people : ctx.empty(ctx.uiText('No matching people.')),
        );
        if (response.peopleHasMore)
          results.append(
            ctx.e('p', 'subtle', ctx.uiText('More matches exist. Refine your search.')),
          );
        if (cards.length > 6) {
          const more = ctx.button(
            'Load more',
            () => {
              people.append(...cards.slice(6));
              more.hidden = true;
              cards[6].querySelector('a')?.focus({ preventScroll: true });
            },
            'secondary-button',
          );
          results.append(more);
        }
      })
      .catch((error) => {
        if (!active()) return;
        results.replaceChildren(
          ctx.e('p', 'form-error', ctx.uiText(error.message)),
          ctx.button('Try again', ctx.render),
        );
      });
  }
  return { renderSearch };
}
